import { useCallback, useState } from 'react';

import useEvent from '@lyearn/core/hooks/useEvent';
import ErrorReporter from '@lyearn/core/utils/ErrorReporter';
import { useDeletePostMutation } from '@/features/messages/graphql/mutations/generated/deletePost';
import { useEditPostMutation } from '@/features/messages/graphql/mutations/generated/editPost';
import { MessageActions } from '@/features/messages/hooks/useMessageAction';
import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';

import {
  EditMessageParams,
  MessageActionFunction,
  UseMessageMutationsParams,
  UseMessageMutationsState,
} from './types';

const useMessageMutations = (params: UseMessageMutationsParams) => {
  const { channelId, snackbarPosition } = params;
  const [, executeEditMessage] = useEditPostMutation();
  const [, executeDeleteMessage] = useDeletePostMutation();
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();

  const [state, setState] = useState<UseMessageMutationsState>({
    messageToEditId: null,
    messageToDeleteId: null,
  });

  const onDeleteMessage = useEvent(async (postId: string) => {
    try {
      const response = await executeDeleteMessage({
        postId,
        channelId,
      });
      if (response.data?.deletePost.success) {
        setState((prevState) => ({ ...prevState, messageToDeleteId: null }));
      } else {
        showDefaultErrorSnackbar(snackbarPosition);
      }
    } catch (e) {
      ErrorReporter.error(e);
      showDefaultErrorSnackbar();
    }
  });

  const onEditMessage = useCallback(
    async ({ messageId, isHidden, isPinned }: EditMessageParams) => {
      try {
        const response = await executeEditMessage({
          post: { postId: messageId, channelId, isHidden, isPinned },
        });
        if (!response.data?.editPost?.success) {
          showDefaultErrorSnackbar(snackbarPosition);
        }
      } catch (e) {
        ErrorReporter.error(e);
        showDefaultErrorSnackbar(snackbarPosition);
      }
    },
    [executeEditMessage, channelId, showDefaultErrorSnackbar, snackbarPosition],
  );

  const onAction: MessageActionFunction = useEvent(async (action) => {
    switch (action.type) {
      case MessageActions.Delete: {
        onDeleteMessage(action.payload.id);
        break;
      }
      case MessageActions.ToggleDelete: {
        setState((prevState) => {
          const toReset = action.payload.id === prevState.messageToDeleteId;
          return {
            ...prevState,
            messageToDeleteId: toReset ? null : action.payload.id,
          };
        });
        break;
      }
      case MessageActions.Edit: {
        setState((prevState) => {
          const toReset = action.payload.id === prevState.messageToEditId;
          return {
            ...prevState,
            messageToEditId: toReset ? null : action.payload.id,
          };
        });
        break;
      }
      case MessageActions.Pin: {
        onEditMessage({ messageId: action.payload.id, isPinned: true });
        break;
      }
      case MessageActions.Unpin: {
        onEditMessage({ messageId: action.payload.id, isPinned: false });
        break;
      }
      case MessageActions.Hide: {
        onEditMessage({ messageId: action.payload.id, isHidden: true, isPinned: false });
        break;
      }
      case MessageActions.Unhide: {
        onEditMessage({ messageId: action.payload.id, isHidden: false });
        break;
      }
      default: {
        break;
      }
    }
  });

  return { state, onAction };
};

export default useMessageMutations;
